import { watch, nextTick, type Ref, type ComputedRef } from 'vue'
import type { DisplayCollection } from '@/interfaces/library/partCollection'

type BoolRef = Ref<boolean> | ComputedRef<boolean>

interface SourceCollection {
  id: number
}

export interface SelectedCollectionScrollOptions {
  selectedCollectionId: Ref<number | null>
  isSearchMode: BoolRef
  collections: Ref<SourceCollection[]>
  visibleCollections: ComputedRef<DisplayCollection[]>
  expandedIds: Ref<Set<number>>
  hasMoreCollections: BoolRef
  isLoadingMoreCollections: BoolRef
  estimatedHeightForCollection: (collection: DisplayCollection) => number
  collapseAll: (includeSearchResults: boolean) => void
  loadMoreCollections: () => Promise<void>
  loadPartsForCollection: (collectionId: number) => Promise<void>
  setScrollTop: (top: number) => void
  getItemElement: (id: number) => HTMLElement | null
  scrollRef: Ref<HTMLElement | null>
}

export interface SelectedCollectionScrollState {
  scrollSelectedCollectionIntoView: (collectionId: number | null) => Promise<void>
}

export function useSelectedCollectionScroll(
  options: SelectedCollectionScrollOptions
): SelectedCollectionScrollState {
  function estimateCollectionOffsetTop(collectionId: number): number | null {
    let offset = 0

    for (const collection of options.visibleCollections.value) {
      if (collection.id === collectionId) {
        return offset
      }

      offset += options.estimatedHeightForCollection(collection)
    }

    return null
  }

  async function waitForScrollContainer(maxTries = 10): Promise<HTMLElement | null> {
    for (let i = 0; i < maxTries; i += 1) {
      if (options.scrollRef.value) return options.scrollRef.value
      await nextTick()
    }

    return null
  }

  async function waitForRenderedCollectionElement(
    collectionId: number,
    maxTries = 10
  ): Promise<HTMLElement | null> {
    for (let i = 0; i < maxTries; i += 1) {
      const el = options.getItemElement(collectionId)
      if (el) return el

      await nextTick()
    }

    return null
  }

  async function ensureSelectedCollectionLoaded(collectionId: number): Promise<void> {
    while (
      !options.collections.value.some((collection) => collection.id === collectionId) &&
      options.hasMoreCollections.value &&
      !options.isLoadingMoreCollections.value
    ) {
      await options.loadMoreCollections()
      await nextTick()
    }
  }

  async function prepareSelectedCollection(collectionId: number): Promise<boolean> {
    await ensureSelectedCollectionLoaded(collectionId)

    const exists = options.collections.value.some(
      (collection) => collection.id === collectionId
    )
    if (!exists) return false

    options.collapseAll(false)
    await options.loadPartsForCollection(collectionId)
    options.expandedIds.value = new Set([collectionId])

    await nextTick()
    await nextTick()

    return true
  }

  async function jumpNearSelectedCollection(collectionId: number): Promise<void> {
    const estimatedTop = estimateCollectionOffsetTop(collectionId)
    if (estimatedTop == null) return

    options.setScrollTop(estimatedTop)
    await nextTick()
    await nextTick()
  }

  async function alignSelectedCollectionPrecisely(collectionId: number): Promise<void> {
    const scroller = await waitForScrollContainer()
    if (!scroller) return

    const el = await waitForRenderedCollectionElement(collectionId)
    if (!el) return

    const scrollerEl: HTMLElement = scroller
    const itemEl: HTMLElement = el

    function correct() {
      const scrollerRect = scrollerEl.getBoundingClientRect()
      const elRect = itemEl.getBoundingClientRect()
      const delta = elRect.top - scrollerRect.top - 8
      if (Math.abs(delta) > 1) {
        options.setScrollTop(scrollerEl.scrollTop + delta)
      }
    }

    correct()
    await nextTick()

    // Wait for useMeasuredVirtualList's RAF-based height flush to settle,
    // then correct again for any scrollTop drift it introduced.
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
    await nextTick()

    correct()
  }

  async function scrollSelectedCollectionIntoView(
    collectionId: number | null
  ): Promise<void> {
    if (!collectionId || options.isSearchMode.value) return

    const prepared = await prepareSelectedCollection(collectionId)
    if (!prepared) return

    if (!options.scrollRef.value) {
      await nextTick()
      document.getElementById(`part-collection-${collectionId}`)?.scrollIntoView({ block: 'start' })
      return
    }

    await jumpNearSelectedCollection(collectionId)
    await alignSelectedCollectionPrecisely(collectionId)
  }

  watch(options.selectedCollectionId, scrollSelectedCollectionIntoView)

  return {
    scrollSelectedCollectionIntoView
  }
}